/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2021, 2024. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
'use strict';

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _toConsumableArray(r) { return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _iterableToArray(r) { if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r); }
function _arrayWithoutHoles(r) { if (Array.isArray(r)) return _arrayLikeToArray(r); }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
var jsonschema = require('jsonschema');

/*
 * A JSON schema validator with a jsen-style interface.
 */

/*
 * Compiles the given JSON schema into a validator function. Options include:
 * - allErrors: Whether or not to return all errors (true) or stop at the first error (false)
 * - schemas: Any additional schemas referenced by the main schema. This is an object containing properties where
 *    the key is the id of the referenced schema (e.g. for "$ref": "#/otherschema", use {otherschema: ...}).
 *
 * Usage:
 * const validate = jsonValidator.compile(schema, options);
 * if (validate(input)) {
 *   // input is valid
 *   console.log('Valid!');
 * } else {
 *   // input is invalid, details in validate.errors
 *   console.log(`Validation failed: ${validate.errors}`);
 * }
 */
exports.compile = function (schema, options) {
  options = options || {};
  var _validate = function validate(input) {
    var validator = new jsonschema.Validator();
    if (options.schemas) {
      // add additional referenced schemas (e.g. "$ref": "#/otherschema") so validator can find them
      for (var i in options.schemas) {
        validator.addSchema(adaptSchema(options.schemas[i]), "/#/".concat(i));
      }
    }
    var result = validator.validate(input, adaptSchema(schema), {
      nestedErrors: true
    });

    // jsonschema doesn't have an 'allErrors' equivalent; extract the first error if not true
    var errors = result.errors || [];
    if (!options.allErrors && errors.length > 1) {
      errors = errors.slice(0, 1);
    }
    _validate.errors = adaptErrors(errors);
    return result.valid;
  };
  return _validate;
};

/*
 * Adapts (converts) jsonschema validation error objects into jsen-style error objects, which our code expects.
 */
function adaptErrors(errors) {
  return errors.map(function (e) {
    var _e$argument;
    // for allOf errors, grab the nested error
    e = ((_e$argument = e.argument) === null || _e$argument === void 0 || (_e$argument = _e$argument.valid) === null || _e$argument === void 0 ? void 0 : _e$argument.errors[0]) || e;
    var path;
    if (e.name === 'required') {
      // e.property is the dot-delimited property path to the object missing the required property, starting with
      // "instance", e.g. "instance.obj1", and e.argument is the missing property name
      path = [].concat(_toConsumableArray(e.property.split('.').slice(1)), [e.argument]);
    } else {
      path = e.path || [];
    }
    return {
      keyword: e.name,
      path: path.join('.')
    };
  });
}

/*
 * Adapts (converts) the given schema into one that jsonschema can handle. We have existing schemas (e.g. templates, registered
 * brokers) that we must maintain backward compatibility with that are not compatible with jsonschema as-is as they are not
 * valid JSON schema. This converts the schema into an jsonschema-friendly format.
 */
function adaptSchema(schema) {
  // clone so we don't modify original schema
  schema = JSON.parse(JSON.stringify(schema));
  for (var i in schema.properties) {
    var prop = schema.properties[i];
    // ignore invalid properties. v1 validator adds extra string properties, which jsonschema interprets as schema refs
    if (!['object', 'bool'].includes(_typeof(prop))) {
      delete schema.properties[i];
      continue;
    }
    // some brokers (e.g. git) use empty enums (enum: []) to represent a select field where the values
    // are loaded at runtime. This will fail validation - strip (ignore) invalid enums
    if (prop.enum !== undefined && (!Array.isArray(prop.enum) || prop.enum.length === 0)) {
      delete schema.properties[i];
      continue;
    }
    // strip (ignore) invalid non-array required properties. Some brokers (e.g. jenkins) specify required: true on the properties instead
    // of an array of required props.
    if (prop.required !== undefined && !Array.isArray(prop.required)) {
      delete prop.required;
    }
  }
  return schema;
}